// Stable ids keep saved assets/formulas compatible. Availability comes from the data,
// never from this vocabulary: adding a label here must not invent an extracted field.
export const ANALYSIS_FIELDS = {
  stream: { id: 'stream', label: 'Data stream', type: 'dimension' },
  date: { id: 'date', label: 'Day', type: 'dimension' },
  year: { id: 'year', label: 'Year', type: 'dimension' },
  month: { id: 'month', label: 'Month', type: 'dimension' },
  quarter: { id: 'quarter', label: 'Quarter', type: 'dimension' },
  week: { id: 'week', label: 'Week', type: 'dimension' },
  account: { id: 'account', label: 'Account name', type: 'dimension' },
  campaign: { id: 'campaign', label: 'Campaign', type: 'dimension', reportingLevel: 'campaign' },
  campaignId: { id: 'campaignId', label: 'Campaign ID', type: 'dimension', reportingLevel: 'campaign' },
  adset: { id: 'adset', label: 'Ad set', type: 'dimension', reportingLevel: 'adset' },
  adsetId: { id: 'adsetId', label: 'Ad set ID', type: 'dimension', reportingLevel: 'adset' },
  ad: { id: 'ad', label: 'Ad', type: 'dimension', reportingLevel: 'ad' },
  adId: { id: 'adId', label: 'Ad ID', type: 'dimension', reportingLevel: 'ad' },
  entity: { id: 'entity', label: 'Entity', type: 'dimension' },
  platform: { id: 'platform', label: 'Platform', type: 'dimension' },
  currency: { id: 'currency', label: 'Currency', type: 'dimension' },
  reach: { id: 'reach', label: 'Reach', type: 'metric' },
  frequency: { id: 'frequency', label: 'Avg. frequency', type: 'metric' },
  impressions: { id: 'impressions', label: 'Impressions', type: 'metric' },
  spend: { id: 'spend', label: 'Spend', type: 'metric' },
  clicks: { id: 'clicks', label: 'Clicks', type: 'metric' },
  efficiency: { id: 'efficiency', label: 'Reach / impression', type: 'metric', calculated: true },
};

export const RETIRED_METRICS = ['frequency', 'cpm', 'cpc', 'ctr'];
export const isRetiredMetric = id => RETIRED_METRICS.includes(String(id).replace(/^metric:/, ''));

export function selectableAnalysisFields(discovered = [], calculated = []) {
  return Object.fromEntries([
    ...discovered.filter(field => field.id !== 'entity' && !isRetiredMetric(field.id))
      .map(field => [field.id, { ...ANALYSIS_FIELDS[field.id], ...field,
        label: ANALYSIS_FIELDS[field.id]?.label || field.label }]),
    ...calculated.map(field => [field.id, { ...field, label: field.name, calculated: true }]),
  ]);
}

export function activeFilterIds(dimensionFilters = {}, operators = {}) {
  return [...new Set([...Object.keys(dimensionFilters), ...Object.keys(operators)])]
    .filter(id => (dimensionFilters[id] || []).length > 0 || (operators[id] || 'list') !== 'list');
}

// Show obsolete/absent saved choices so users can remove them deliberately. Do not
// silently drop a column, mapping or condition when its source changes.
export function retainedAnalysisFields(available, selected = [], saved = {}) {
  return { ...Object.fromEntries(selected.filter(id => !available[id]).map(id => [id, {
    ...ANALYSIS_FIELDS[id], ...saved[id], id,
    label: saved[id]?.label || ANALYSIS_FIELDS[id]?.label || id,
    type: saved[id]?.type || ANALYSIS_FIELDS[id]?.type || 'dimension', unavailable: true,
  }])), ...available };
}

export function unavailableFieldIds(available, selected) {
  return [...new Set(selected)].filter(id => !available[id]);
}

export function formulaFieldAliases(row = {}) {
  const dynamic = Object.fromEntries(Object.keys(row).flatMap(id => [
    [id.toLowerCase(), id],
    ...(/^(breakdown|metric):/.test(id) ? [[id.split(':').slice(1).join(':').replaceAll('_', ' ').toLowerCase(), id]] : []),
  ]));
  return { ...dynamic, date: 'date', account: 'account', ...Object.fromEntries(Object.values(ANALYSIS_FIELDS).flatMap(field => [
    [field.id.toLowerCase(), field.id], [field.label.toLowerCase(), field.id],
  ])) };
}
